'use client';

import { useEffect, useRef } from 'react';

import { createDraftApplicationFromIntentAction } from '@kit/auditions/server';

import {
  pendingAuditionIntent,
  resolveApplyJourneyTarget,
} from '~/_lib/apply-journey';

/**
 * Claims a pending apply intent as a real draft registration, as early as
 * possible after authentication.
 *
 * Why this exists: the apply journey used to be carried entirely by the `next`
 * query param, and `next` was destroyed at three separate hops between signup
 * and the competition form (the `/welcome` bounce, the role picker's redirect,
 * and the setup wizard's own navigation). The only recovery ran at the FINAL
 * step of the dancer setup wizard, so anyone who quit mid-wizard left no
 * registration at all. Measured on prod for GDO Finals 2026: 309 accounts
 * created inside the registration window with zero applications, 233 of them
 * with a half-filled profile and 116 with their media already uploaded.
 *
 * Threading `next` through every hop is the fragile fix — one new redirect
 * re-opens the hole. This component removes the dependency instead: the moment
 * an authenticated user renders any surface that can see the stored intent, the
 * draft application is created. From then on the registration exists in the
 * database, so the organizer sees the entrant, the resume banner can route them
 * back, and abandonment costs a half-filled form rather than the whole lead.
 *
 * The action is idempotent (it returns the existing application when one
 * matches), so re-mounting is harmless. The intent is deliberately NOT cleared
 * here — the wizard and the resume banner still read it to route the user to
 * the right competition. It is cleared where the journey actually completes.
 */
export function ApplyIntentClaimer() {
  const claimed = useRef(false);

  useEffect(() => {
    if (claimed.current) return;

    // Evict an expired intent FIRST. It has to run before the read below,
    // because a stale intent makes `read()` return null and the early return
    // then skips the sweep entirely — leaving the expired entry in place,
    // which is the one case the sweep was written for.
    pendingAuditionIntent.sweepStale();

    // Read `next` off the live URL rather than `useSearchParams()`: this
    // component mounts on statically-rendered public pages, and that hook
    // forces the whole subtree into a Suspense boundary there. Inside an
    // effect the value is client-only anyway, so the hook buys nothing.
    const nextParam = new URLSearchParams(window.location.search).get('next');

    // Both signals, same precedence the wizard uses: the URL `next` is the
    // more specific one, the stored intent is what survives a redirect that
    // drops it. Either alone leaves a real gap — a visitor who reaches
    // sign-in directly from an emailed link never wrote localStorage, and a
    // visitor bounced through the role picker has lost the URL.
    const target = resolveApplyJourneyTarget(
      nextParam,
      pendingAuditionIntent.read(),
    );
    if (!target || target.surface !== 'competition') return;

    claimed.current = true;

    void createDraftApplicationFromIntentAction({
      auditionSlug: target.slug,
      ...(target.styleEntryIds && target.styleEntryIds.length > 0
        ? { styleEntryIds: target.styleEntryIds }
        : {}),
      // Carry the access code: a code-gated competition mints the invitation
      // at registration (redeem-at-register), so dropping it here would make
      // the invitation gate reject every finalist who arrived by invite link.
      ...(target.code ? { accessCode: target.code } : {}),
    })
      .then((result) => {
        if (!result || !('success' in result) || result.success) return;

        // Only a PERMANENT refusal drops the intent.
        //
        // Not every `{ success: false }` is a decision. The invitation gate
        // resolves through the caller's DANCER profile, which the setup wizard
        // creates — so an invited finalist is refused on the first
        // authenticated page they see and accepted minutes later on the very
        // same intent. Clearing there would delete the registration of exactly
        // the people this whole change exists to save, and silently. The
        // action marks those refusals `retryable`, and anything unmarked is
        // final (a competition that does not exist, an input the schema
        // rejects), so it is safe to forget.
        if (result.retryable === true) {
          // Defensive only, and worth saying so. The effect has `[]` deps and
          // never re-runs on one instance, so a later retry comes from a NEW
          // mount whose ref starts false regardless.
          //
          // That holds under StrictMode too. Its double-invoke is synchronous
          // within the commit, so the second run reads the ref already true and
          // returns before this promise settles — and setting a ref to false
          // could only ever ADD a call, never suppress one. Measured: one
          // action call with this line and one without it.
          //
          // So the line is inert on every path the component has today. It
          // earns its place against a future change that gives the effect a
          // reason to re-run, not against anything shipping now. Deleting it
          // changes no test, which is a fact about the line, not a gap in the
          // suite.
          claimed.current = false;
          return;
        }

        pendingAuditionIntent.clear();
      })
      .catch(() => {
        // A THROW is different: the action never reached a decision (network,
        // auth expiry). Allow the next mount to try again — the action is
        // idempotent, so a duplicate attempt costs nothing.
        claimed.current = false;
      });
  }, []);

  return null;
}
